import { useSyncExternalStore } from 'react';

interface InstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

type InstallResult = 'accepted' | 'dismissed' | 'unavailable' | null;

interface PwaState {
  installed: boolean;
  canInstall: boolean;
  installing: boolean;
  result: InstallResult;
  online: boolean;
}

const displayMode = window.matchMedia('(display-mode: standalone)');
let promptEvent: InstallPromptEvent | null = null;
let started = false;
let state: PwaState = {
  installed: displayMode.matches || (navigator as Navigator & { standalone?: boolean }).standalone === true,
  canInstall: false,
  installing: false,
  result: null,
  online: navigator.onLine,
};
const subscribers = new Set<() => void>();

function updateState(update: Partial<PwaState>) {
  state = { ...state, ...update };
  subscribers.forEach((subscriber) => subscriber());
}

function subscribe(subscriber: () => void) {
  subscribers.add(subscriber);
  return () => { subscribers.delete(subscriber); };
}

export function usePwaState() {
  return useSyncExternalStore(subscribe, () => state);
}

export function startPwaSupport() {
  if (started) return;
  started = true;

  window.addEventListener('beforeinstallprompt', (event: Event) => {
    event.preventDefault();
    promptEvent = event as InstallPromptEvent;
    updateState({ canInstall: true, result: null });
  });
  window.addEventListener('appinstalled', () => {
    promptEvent = null;
    updateState({ installed: true, canInstall: false, installing: false, result: null });
  });
  displayMode.addEventListener('change', () => {
    updateState({ installed: displayMode.matches || (navigator as Navigator & { standalone?: boolean }).standalone === true });
  });
  window.addEventListener('online', () => updateState({ online: true }));
  window.addEventListener('offline', () => updateState({ online: false }));

  if (import.meta.env.PROD && 'serviceWorker' in navigator && window.isSecureContext) {
    const register = () => {
      // Only a public offline notice is cached. Auth, lessons and media always use the network.
      void navigator.serviceWorker.register('/sw.js', { scope: '/', updateViaCache: 'none' }).catch(() => {
        // Browser installation instructions remain available if registration is restricted.
      });
    };
    if (document.readyState === 'complete') register();
    else window.addEventListener('load', register, { once: true });
  }
}

export async function requestPwaInstall() {
  if (!promptEvent || state.installing) return;
  const currentPrompt = promptEvent;
  promptEvent = null;
  updateState({ installing: true, canInstall: false, result: null });
  try {
    // prompt() must run directly from the user's click and each event is used once.
    await currentPrompt.prompt();
    const { outcome } = await currentPrompt.userChoice;
    updateState({ result: outcome });
  } catch {
    updateState({ result: 'unavailable' });
  } finally {
    updateState({ installing: false });
  }
}
